import { useNavigate, useParams, useMatch } from 'react-router-dom'

export type MasterFormMode = 'list' | 'new' | 'edit' | 'view'

/**
 * Drives the full-page (route-based) add/edit/view flow shared by every
 * Master page.
 *
 * Register the routes per master, all pointing to the same component:
 *   <Route path="/master/xxx"          element={<XxxMaster />} />
 *   <Route path="/master/xxx/new"      element={<XxxMaster />} />
 *   <Route path="/master/xxx/:id/edit" element={<XxxMaster />} />
 *   <Route path="/master/xxx/:id/view" element={<XxxMaster />} /> (optional — only
 *     masters with a full-page detail view need this one)
 *
 * The component then renders the list when `mode === 'list'`, the full-page
 * form for 'new'/'edit' (`isForm`), or a full-page read-only detail view for
 * 'view'. `goAdd` / `goEdit(id)` / `goView(id)` / `goList()` navigate between
 * them.
 */
export function useMasterFormRoute(base: string) {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isNew = useMatch(`${base}/new`) != null
  const isView = useMatch(`${base}/:id/view`) != null
  const mode: MasterFormMode = isNew ? 'new' : isView ? 'view' : id ? 'edit' : 'list'

  return {
    mode,
    editId: id,
    isForm: mode === 'new' || mode === 'edit',
    goAdd: () => navigate(`${base}/new`),
    goEdit: (rid: string) => navigate(`${base}/${rid}/edit`),
    goView: (rid: string) => navigate(`${base}/${rid}/view`),
    goList: () => navigate(base),
  }
}
